import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Skeleton,
  useTheme,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

export interface OfferFormData {
  headline: string;
  description: string;
  content: string;
  leaseLabel: string;
  leaseDownLabel: string;
  financeLabel: string;
  leaseAmount: string;
  term: string;
  leaseTerm: string;
  leaseDown: string;
  financeRate: string;
  financeTerm: string;
  startDate: string;
  endDate: string;
  year: string;
  make: string;
  model: string;
  trim: string;
  drivetrain: string;
  offerUrl: string;
  offerImageUrl: string;
  disclaimer: string;
}

export interface OfferFormErrors {
  headline?: string;
  leaseLabel?: string;
  leaseDownLabel?: string;
  financeLabel?: string;
  leaseAmount?: string;
  term?: string;
  leaseTerm?: string;
  leaseDown?: string;
  financeRate?: string;
  financeTerm?: string;
  endDate?: string;
  year?: string;
  make?: string;
  model?: string;
}

interface OfferFormProps {
  formData: OfferFormData;
  modalType: 'lease' | 'leaseFinance' | 'finance';
  onFormChange: (field: string, value: string) => void;
  errors?: OfferFormErrors;
}

// Validation utility functions
export const validateOfferForm = (formData: OfferFormData, modalType: 'lease' | 'leaseFinance' | 'finance'): OfferFormErrors => {
  const errors: OfferFormErrors = {};

  // Common required fields
  if (!formData.headline?.trim()) {
    errors.headline = 'Offer headline is required';
  }

  if (!formData.endDate?.trim()) {
    errors.endDate = 'End date is required';
  }

  if (!formData.year?.trim()) {
    errors.year = 'Vehicle year is required';
  }

  if (!formData.make?.trim()) {
    errors.make = 'Vehicle make is required';
  }

  if (!formData.model?.trim()) {
    errors.model = 'Vehicle model is required';
  }

  // Lease-specific validation
  if (modalType === 'lease') {
    if (!formData.leaseLabel?.trim()) {
      errors.leaseLabel = 'Lease label is required';
    }
    if (!formData.leaseDownLabel?.trim()) {
      errors.leaseDownLabel = 'Lease down label is required';
    }
    if (!formData.leaseAmount?.trim()) {
      errors.leaseAmount = 'Lease amount is required';
    } else if (isNaN(Number(formData.leaseAmount)) || Number(formData.leaseAmount) <= 0) {
      errors.leaseAmount = 'Lease amount must be a valid positive number';
    }
    if (!formData.term?.trim()) {
      errors.term = 'Term is required';
    } else if (isNaN(Number(formData.term)) || Number(formData.term) <= 0) {
      errors.term = 'Term must be a valid positive number';
    }
    if (!formData.leaseDown?.trim()) {
      errors.leaseDown = 'Down payment is required';
    } else if (isNaN(Number(formData.leaseDown)) || Number(formData.leaseDown) < 0) {
      errors.leaseDown = 'Down payment must be a valid number';
    }
  }

  // Finance-specific validation
  if (modalType === 'finance') {
    if (!formData.financeLabel?.trim()) {
      errors.financeLabel = 'Finance label is required';
    }
    if (!formData.financeRate?.trim()) {
      errors.financeRate = 'Finance rate is required';
    } else if (isNaN(Number(formData.financeRate)) || Number(formData.financeRate) < 0) {
      errors.financeRate = 'Finance rate must be a valid positive number';
    }
    if (!formData.financeTerm?.trim()) {
      errors.financeTerm = 'Finance term is required';
    } else if (isNaN(Number(formData.financeTerm)) || Number(formData.financeTerm) <= 0) {
      errors.financeTerm = 'Finance term must be a valid positive number';
    }
  }

  // Lease and Finance combined validation
  if (modalType === 'leaseFinance') {
    // Lease fields
    if (!formData.leaseLabel?.trim()) {
      errors.leaseLabel = 'Lease label is required';
    }
    if (!formData.leaseDownLabel?.trim()) {
      errors.leaseDownLabel = 'Lease down label is required';
    }
    if (!formData.leaseAmount?.trim()) {
      errors.leaseAmount = 'Lease amount is required';
    } else if (isNaN(Number(formData.leaseAmount)) || Number(formData.leaseAmount) <= 0) {
      errors.leaseAmount = 'Lease amount must be a valid positive number';
    }
    if (!formData.leaseTerm?.trim()) {
      errors.leaseTerm = 'Lease term is required';
    } else if (isNaN(Number(formData.leaseTerm)) || Number(formData.leaseTerm) <= 0) {
      errors.leaseTerm = 'Lease term must be a valid positive number';
    }
    if (!formData.leaseDown?.trim()) {
      errors.leaseDown = 'Lease down/Due at signing is required';
    } else if (isNaN(Number(formData.leaseDown)) || Number(formData.leaseDown) < 0) {
      errors.leaseDown = 'Lease down/Due at signing must be a valid number';
    }

    // Finance fields
    if (!formData.financeLabel?.trim()) {
      errors.financeLabel = 'Finance label is required';
    }
    if (!formData.financeRate?.trim()) {
      errors.financeRate = 'Finance rate is required';
    } else if (isNaN(Number(formData.financeRate)) || Number(formData.financeRate) < 0) {
      errors.financeRate = 'Finance rate must be a valid positive number';
    }
    if (!formData.financeTerm?.trim()) {
      errors.financeTerm = 'Finance term is required';
    } else if (isNaN(Number(formData.financeTerm)) || Number(formData.financeTerm) <= 0) {
      errors.financeTerm = 'Finance term must be a valid positive number';
    }
  }

  return errors;
};

export const hasValidationErrors = (errors: OfferFormErrors): boolean => {
  return Object.keys(errors).length > 0;
};

// Image Preview Component
const ImagePreview = ({ imageUrl, isEditing, onClick }: { imageUrl: string; isEditing: boolean; onClick?: () => void }) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [imageUrl]);

  // Helper function to truncate URL
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength)}...`;
  };

  if (!imageUrl || imageUrl.trim() === '') {
    return (
      <Box
        sx={{
          width: isEditing ? 120 : 150,
          height: isEditing ? 80 : 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.action.hover,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: theme.palette.divider,
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.primary' }}>
          <ImageIcon sx={{ fontSize: 32, mb: 0.5 }} />
          <Typography variant="caption" display="block">
            No Image
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Tooltip 
      title={truncateUrl(imageUrl, 60)} 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 400,
            fontSize: '0.75rem',
            wordBreak: 'break-all'
          }
        }
      }}
    >
      <Box
        sx={{
          width: isEditing ? 120 : 150,
          height: isEditing ? 80 : 100,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
            borderColor: theme.palette.divider,
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': onClick ? {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows[4]
          } : {}
        }}
        onClick={onClick}
      >
        {imageLoading && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
        )}
        {imageError ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.action.hover,
              color: 'text.primary',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <ImageIcon sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="caption" display="block">
                Failed to load
              </Typography>
            </Box>
          </Box>
        ) : (
          <img
            src={imageUrl}
            alt="Offer"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: imageLoading ? 'none' : 'block',
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
        )}
        {onClick && !imageError && !imageLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.action.selected,
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              '.MuiBox-root:hover &': {
                opacity: 1
              }
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.common.white, fontSize: '10px' }}>
              ↗
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

const OfferForm: React.FC<OfferFormProps> = ({
  formData,
  modalType,
  onFormChange,
  errors = {},
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Common Fields */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Offer Headline *
        </Typography>
        <TextField
          fullWidth
          value={formData.headline}
          onChange={e => onFormChange('headline', e.target.value)}
          variant="outlined"
          size="small"
          error={!!errors.headline}
          helperText={errors.headline}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
            },
          }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Offer Description
        </Typography>
        <TextField
          fullWidth
          value={formData.description}
          onChange={e => onFormChange('description', e.target.value)}
          multiline
          rows={2}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
            },
          }}
        />
      </Box>

      {/* <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Offer Content
        </Typography>
        <TextField
          fullWidth
          value={formData.content}
          onChange={e => onFormChange('content', e.target.value)}
          multiline
          rows={2}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
            },
          }}
        />
      </Box> */}

      {/* Lease-specific Fields */}
      {modalType === 'lease' && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Label *
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.leaseLabel}>
                <Select
                  value={formData.leaseLabel}
                  onChange={e => onFormChange('leaseLabel', e.target.value)}
                >
                  <MenuItem value="Lease For">Lease For</MenuItem>
                  <MenuItem value="Lease From">Lease From</MenuItem>
                </Select>
                {errors.leaseLabel && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.leaseLabel}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Down Label *
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.leaseDownLabel}>
                <Select
                  value={formData.leaseDownLabel}
                  onChange={e => onFormChange('leaseDownLabel', e.target.value)}
                >
                  <MenuItem value="Down">Down</MenuItem>
                  <MenuItem value="Due at Signing">Due at Signing</MenuItem>
                </Select>
                {errors.leaseDownLabel && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.leaseDownLabel}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Amount *
              </Typography>
              <TextField
                fullWidth
                value={formData.leaseAmount}
                onChange={e => onFormChange('leaseAmount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.leaseAmount}
                helperText={errors.leaseAmount}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Term *
              </Typography>
              <TextField
                fullWidth
                value={formData.term}
                onChange={e => onFormChange('term', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Months</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.term}
                helperText={errors.term}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Down Payment *
            </Typography>
            <TextField
              fullWidth
              value={formData.leaseDown}
              onChange={e => onFormChange('leaseDown', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              error={!!errors.leaseDown}
              helperText={errors.leaseDown}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '8px 14px',
                },
              }}
            />
          </Box>
        </>
      )}

      {/* Lease and Finance-specific Fields */}
      {modalType === 'leaseFinance' && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Label *
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.leaseLabel}>
                <Select
                  value={formData.leaseLabel}
                  onChange={e => onFormChange('leaseLabel', e.target.value)}
                >
                  <MenuItem value="Lease For">Lease For</MenuItem>
                  <MenuItem value="Lease From">Lease From</MenuItem>
                </Select>
                {errors.leaseLabel && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.leaseLabel}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Finance Label *
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.financeLabel}>
                <Select
                  value={formData.financeLabel}
                  onChange={e => onFormChange('financeLabel', e.target.value)}
                >
                  <MenuItem value="Finance For">Finance For</MenuItem>
                  <MenuItem value="Finance From">Finance From</MenuItem>
                </Select>
                {errors.financeLabel && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.financeLabel}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Down Label *
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.leaseDownLabel}>
                <Select
                  value={formData.leaseDownLabel}
                  onChange={e => onFormChange('leaseDownLabel', e.target.value)}
                >
                  <MenuItem value="Down">Down</MenuItem>
                  <MenuItem value="Due at Signing">Due at Signing</MenuItem>
                </Select>
                {errors.leaseDownLabel && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.leaseDownLabel}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box>
              {/* Empty box for layout balance */}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Amount *
              </Typography>
              <TextField
                fullWidth
                value={formData.leaseAmount}
                onChange={e => onFormChange('leaseAmount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.leaseAmount}
                helperText={errors.leaseAmount}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Term *
              </Typography>
              <TextField
                fullWidth
                value={formData.leaseTerm}
                onChange={e => onFormChange('leaseTerm', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Months</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.leaseTerm}
                helperText={errors.leaseTerm}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Lease Down/Due at Signing *
              </Typography>
              <TextField
                fullWidth
                value={formData.leaseDown}
                onChange={e => onFormChange('leaseDown', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.leaseDown}
                helperText={errors.leaseDown}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Finance Rate *
              </Typography>
              <TextField
                fullWidth
                value={formData.financeRate}
                onChange={e => onFormChange('financeRate', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">% APR</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.financeRate}
                helperText={errors.financeRate}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Finance Term *
            </Typography>
            <TextField
              fullWidth
              value={formData.financeTerm}
              onChange={e => onFormChange('financeTerm', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Months</InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              error={!!errors.financeTerm}
              helperText={errors.financeTerm}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '8px 14px',
                },
              }}
            />
          </Box>
        </>
      )}

      {/* Finance-specific Fields */}
      {modalType === 'finance' && (
        <>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Finance Label *
            </Typography>
            <FormControl fullWidth size="small" error={!!errors.financeLabel}>
              <Select
                value={formData.financeLabel}
                onChange={e => onFormChange('financeLabel', e.target.value)}
              >
                <MenuItem value="Finance For">Finance For</MenuItem>
                <MenuItem value="Finance From">Finance From</MenuItem>
              </Select>
              {errors.financeLabel && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                  {errors.financeLabel}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Finance Rate *
              </Typography>
              <TextField
                fullWidth
                value={formData.financeRate}
                onChange={e => onFormChange('financeRate', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">% APR</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.financeRate}
                helperText={errors.financeRate}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Finance Term *
              </Typography>
              <TextField
                fullWidth
                value={formData.financeTerm}
                onChange={e => onFormChange('financeTerm', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Months</InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                error={!!errors.financeTerm}
                helperText={errors.financeTerm}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Common Date Fields */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Start Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="Start Date"
              value={formData.startDate ? DateTime.fromISO(formData.startDate, { setZone: true }).toLocal() : null}
              onChange={(newValue: DateTime | null) =>
                onFormChange(
                  'startDate',
                  newValue ? newValue.startOf('day').toUTC().toISO() ?? '' : '',
                )
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      padding: '8px 14px',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            End Date *
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="End Date"
              value={formData.endDate ? DateTime.fromISO(formData.endDate, { setZone: true }).toLocal() : null}
              onChange={(newValue: DateTime | null) =>
                onFormChange(
                  'endDate',
                  newValue ? newValue.startOf('day').toUTC().toISO() ?? '' : '',
                )
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: !!errors.endDate,
                  helperText: errors.endDate,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      padding: '8px 14px',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      {/* Vehicle Information */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Vehicle Year *
          </Typography>
          <TextField
            fullWidth
            value={formData.year}
            onChange={e => onFormChange('year', e.target.value)}
            variant="outlined"
            size="small"
            error={!!errors.year}
            helperText={errors.year}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Vehicle Make *
          </Typography>
          <TextField
            fullWidth
            value={formData.make}
            onChange={e => onFormChange('make', e.target.value)}
            variant="outlined"
            size="small"
            error={!!errors.make}
            helperText={errors.make}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Vehicle Model *
          </Typography>
          <TextField
            fullWidth
            value={formData.model}
            onChange={e => onFormChange('model', e.target.value)}
            variant="outlined"
            size="small"
            error={!!errors.model}
            helperText={errors.model}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Vehicle Trim
          </Typography>
          <TextField
            fullWidth
            value={formData.trim}
            onChange={e => onFormChange('trim', e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Drivetrain
          </Typography>
          <TextField
            fullWidth
            value={formData.drivetrain}
            onChange={e => onFormChange('drivetrain', e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Offer URL
        </Typography>
        <TextField
          fullWidth
          value={formData.offerUrl}
          onChange={e => onFormChange('offerUrl', e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
            },
          }}
        />
      </Box>

      {/* Offer Image */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Offer Image
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <ImagePreview 
              imageUrl={formData.offerImageUrl} 
              isEditing={true}
              onClick={formData.offerImageUrl ? () => window.open(formData.offerImageUrl, '_blank') : undefined}
            />
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                value={formData.offerImageUrl}
                onChange={e => onFormChange('offerImageUrl', e.target.value)}
                placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                size="small"
                helperText="Enter a valid image URL to display the offer image. Click image to open in new tab."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 14px',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>



      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Disclaimer Text
        </Typography>
        <TextField
          fullWidth
          value={formData.disclaimer}
          onChange={e => onFormChange('disclaimer', e.target.value)}
          multiline
          rows={3}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default OfferForm;
